import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { MarkdownContent } from '@/components/ui/markdown-content';
import { Release, SubItem, Client, Status, Environment, SubItemStatus, Source } from '@/types';
import { useAccessControl } from '@/hooks/useAccessControl';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  NativeDialog,
  NativeDialogContent,
  NativeDialogTitle,
  NativeDialogDescription,
  NativeDialogFooter,
  NativeDialogHeader
} from '@/components/ui/native-dialog';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  User, 
  Globe, 
  Edit,
  Plus,
  Trash,
  AlertTriangle,
} from 'lucide-react';
import { DetailsItem, DetailsLink, DetailsText } from '@/components/ui/details';
import SubItemList, { SubItemListHandle } from '@/components/SubItemList';
import InlineReleaseForm from '@/components/InlineReleaseForm';
import ReleaseStatusProgress from '@/components/ReleaseStatusProgress';
import { formatDate } from '@/lib/utils';
import { 
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

// Status colors for badges (white bg with colored text)
const statusColors = {
  'released': 'bg-white text-[#10b981] border border-[#10b981]', // release-released
  'ready for release': 'bg-white text-[#3b82f6] border border-[#3b82f6]', // release-ready
  'not ready': 'bg-white text-[#f59e0b] border border-[#f59e0b]', // release-not-ready
  'on hold': 'bg-white text-[#ef4444] border border-[#ef4444]', // release-on-hold
  // SubItem status colors
  'not started': 'bg-white text-[#64748b] border border-[#64748b]',
  'in progress': 'bg-white text-[#f59e0b] border border-[#f59e0b]',
  'ready': 'bg-white text-[#10b981] border border-[#10b981]'
};

// Header background colors for status sections
const statusHeaderColors = {
  'released': 'bg-[#10b981] text-white', // green for released
  'ready for release': 'bg-[#3b82f6] text-white', // blue for ready
  'not ready': 'bg-[#f59e0b] text-white', // yellow/orange for not ready
  'on hold': 'bg-[#ef4444] text-white' // red for on hold
};

// Border colors for status-based card styling
const statusBorderColors = {
  'released': 'border-[#10b981]', // green for released
  'ready for release': 'border-[#3b82f6]', // blue for ready
  'not ready': 'border-[#f59e0b]', // yellow/orange for not ready
  'on hold': 'border-[#ef4444]' // red for on hold
};

const environmentColors = {
  'staging': 'bg-purple-100 text-purple-800',
  'live': 'bg-red-100 text-red-800'
};

type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

interface SubItemRow {
  id: string;
  name: string;
  status: string;
  source: string;
  approved_by: string | null;
  type: string | null;
  source_link: string | null;
  pages_affected: Json;
  collections_affected: Json;
  components_affected: Json;
  notes: string | null;
  release_id: string | null;
}

const ReleasePage: React.FC = () => {
  const { clientId, releaseId } = useParams<{ clientId: string, releaseId: string }>();
  const { isAdmin } = useAccessControl();
  const [release, setRelease] = useState<Release | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showReleaseForm, setShowReleaseForm] = useState(false);
  const subItemListRef = React.useRef<SubItemListHandle>(null);
  const [showDeleteReleaseConfirm, setShowDeleteReleaseConfirm] = useState(false);
  const [showDeleteSubItemConfirm, setShowDeleteSubItemConfirm] = useState(false);
  const [subItemToDelete, setSubItemToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingRelease, setEditingRelease] = useState<Release | null>(null);

  // Fetch release and client data
  useEffect(() => {
    const fetchReleaseData = async () => {
      if (!clientId || !releaseId) return;
      
      setIsLoading(true);
      try {
        // Fetch release data
        const { data: releaseData, error: releaseError } = await supabase
          .from('releases')
          .select('*')
          .eq('id', releaseId)
          .eq('client_id', clientId)
          .single();
        
        if (releaseError || !releaseData) {
          throw releaseError || new Error('Release not found');
        }

        // Fetch client data
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('*')
          .eq('id', clientId)
          .single();
        
        if (clientError || !clientData) {
          throw clientError || new Error('Client not found');
        }

        // Fetch sub-items for this release
        const { data: subItemsData, error: subItemsError } = await supabase
          .from('sub_items')
          .select('*')
          .eq('release_id', releaseId);
        
        if (subItemsError) {
          throw subItemsError;
        }

        // Process sub-items
        const processedSubItems: SubItem[] = (subItemsData || []).map((item: SubItemRow) => ({
          id: item.id,
          name: item.name,
          status: item.status as SubItemStatus,
          source: item.source as Source,
          approvedBy: item.approved_by || '',
          type: item.type || '',
          sourceLink: item.source_link || '',
          pagesAffected: Array.isArray(item.pages_affected) ? item.pages_affected as string[] : [],
          collectionsAffected: Array.isArray(item.collections_affected) ? item.collections_affected as string[] : [],
          componentsAffected: Array.isArray(item.components_affected) ? item.components_affected as string[] : [],
          notes: item.notes || '',
          releaseId: item.release_id || ''
        }));

        // Process release data
        const processedRelease: Release = {
          id: releaseData.id,
          version: releaseData.version,
          status: releaseData.status as Status,
          environment: releaseData.environment as Environment,
          brdLink: releaseData.brd_link || '',
          approvedBy: releaseData.approved_by || '',
          prework: releaseData.prework || '',
          notes: releaseData.notes || '',
          createdAt: new Date(releaseData.created_at),
          plannedReleaseDate: releaseData.planned_release_date ? new Date(releaseData.planned_release_date) : undefined,
          subItems: processedSubItems,
          clientId: releaseData.client_id,
          ownerId: releaseData.owner_id,
        };

        setRelease(processedRelease);
        setClient(clientData as Client);
      } catch (error) {
        console.error('Error fetching release data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load release information',
          variant: 'destructive',
        });
        navigate(`/clients/${clientId}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReleaseData();
  }, [clientId, releaseId, navigate, toast]);

  const handleBackClick = () => {
    navigate(`/clients/${clientId}`);
  };

  // Function to update the release
  const updateRelease = async (updatedRelease: Release) => {
    if (!clientId || !releaseId) return;
    
    setIsUpdating(true);
    try {
      // Update release in database
      const { error } = await supabase
        .from('releases')
        .update({
          version: updatedRelease.version,
          status: updatedRelease.status,
          environment: updatedRelease.environment,
          brd_link: updatedRelease.brdLink,
          approved_by: updatedRelease.approvedBy,
          prework: updatedRelease.prework,
          notes: updatedRelease.notes,
          planned_release_date: updatedRelease.plannedReleaseDate ? updatedRelease.plannedReleaseDate.toISOString() : null,
          owner_id: updatedRelease.ownerId
        })
        .eq('id', releaseId);
      
      if (error) throw error;
      
      // Update local state
      setRelease(updatedRelease);
      
      toast({
        title: "Release updated",
        description: "Release details have been updated successfully"
      });
    } catch (error) {
      console.error('Error updating release:', error);
      toast({
        title: "Error",
        description: "Failed to update release",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Function to delete the release
  const deleteRelease = async () => {
    if (!clientId || !releaseId || isDeleting) return;
    
    setIsDeleting(true);
    try {
      // First delete all sub-items associated with this release
      const { error: subItemsError } = await supabase
        .from('sub_items')
        .delete()
        .eq('release_id', releaseId);
      
      if (subItemsError) throw subItemsError;
      
      // Then delete the release
      const { error } = await supabase
        .from('releases')
        .delete()
        .eq('id', releaseId);
      
      if (error) throw error;
      
      toast({
        title: "Release deleted",
        description: "The release has been deleted successfully"
      });
      
      // Navigate back to client page
      navigate(`/clients/${clientId}`);
    } catch (error) {
      console.error('Error deleting release:', error);
      toast({
        title: "Error",
        description: "Failed to delete release",
        variant: "destructive"
      });
      setIsDeleting(false);
      setShowDeleteReleaseConfirm(false);
    }
  };
  
  // Function to update a sub-item
  const updateSubItem = async (updatedSubItem: SubItem) => {
    if (!releaseId || !updatedSubItem.id) return;
    
    try {
      // Update sub-item in database
      // Update the sub-item in the database
      const { error } = await supabase
        .from('sub_items')
        .update({
          name: updatedSubItem.name,
          status: updatedSubItem.status,
          source: updatedSubItem.source,
          approved_by: updatedSubItem.approvedBy,
          type: updatedSubItem.type,
          source_link: updatedSubItem.sourceLink,
          pages_affected: updatedSubItem.pagesAffected,
          collections_affected: updatedSubItem.collectionsAffected,
          components_affected: updatedSubItem.componentsAffected,
          notes: updatedSubItem.notes,
          release_id: updatedSubItem.releaseId
        })
        .eq('id', updatedSubItem.id);
      
      if (error) throw error;
      
      // Update the local state
      if (release) {
        setRelease({
          ...release,
          subItems: release.subItems.map(item => 
            item.id === updatedSubItem.id ? updatedSubItem : item
          )
        });
      }
      
      toast({
        title: 'Item updated',
        description: 'The release item has been successfully updated',
      });
    } catch (error) {
      console.error('Error updating sub-item:', error);
      toast({
        title: 'Error',
        description: 'Failed to update the release item',
        variant: 'destructive',
      });
    }
  };

  // Function to delete a sub-item
  const deleteSubItem = async (id: string) => {
    if (!releaseId || !id) return;
    
    try {
      // Delete the sub-item from the database
      const { error } = await supabase
        .from('sub_items')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Update the local state
      if (release) {
        setRelease({
          ...release,
          subItems: release.subItems.filter(item => item.id !== id)
        });
      }
      
      toast({
        title: 'Item deleted',
        description: 'The release item has been successfully deleted',
      });
    } catch (error) {
      console.error('Error deleting sub-item:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete the release item',
        variant: 'destructive',
      });
    }
  };
  
  // Function to add a new sub-item
  const addSubItem = async (newSubItem: SubItem) => {
    if (!releaseId) return;
    
    try {
      // Add the sub-item to the database
      const { data, error } = await supabase
        .from('sub_items')
        .insert({
          id: newSubItem.id,
          name: newSubItem.name,
          status: newSubItem.status,
          source: newSubItem.source,
          approved_by: newSubItem.approvedBy,
          type: newSubItem.type,
          source_link: newSubItem.sourceLink,
          pages_affected: newSubItem.pagesAffected,
          collections_affected: newSubItem.collectionsAffected,
          components_affected: newSubItem.componentsAffected,
          notes: newSubItem.notes,
          release_id: releaseId
        })
        .select();
      
      if (error) throw error;
      
      // Update the local state
      if (release) {
        setRelease({
          ...release,
          subItems: [...release.subItems, newSubItem]
        });
      }
      
      toast({
        title: 'Item added',
        description: 'The new release item has been successfully added',
      });
    } catch (error) {
      console.error('Error adding sub-item:', error);
      toast({
        title: 'Error',
        description: 'Failed to add the new release item',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading release information...</p>
      </div>
    );
  }

  if (!release || !client) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Release not found</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        {/* Left side with breadcrumbs */}
        <div>
          <Breadcrumb>
            <BreadcrumbList>
              {isAdmin && (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/admin">Admin</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                </>
              )}
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={`/clients/${clientId}`}>{client.name} Releases</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Release v{release.version}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        
        {/* Right side with status progress */}
        <div className="flex-shrink-0">
          <ReleaseStatusProgress currentStatus={release.status as Status} />
        </div>
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6">
        {/* Release Details Card */}
        <Card className={`bg-white border-2 rounded-lg overflow-hidden ${statusBorderColors[release.status as Status]}`}>
          <div className={`p-4 ${statusHeaderColors[release.status as Status]}`}>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-medium">
                  v{release.version} - {formatDate(release.plannedReleaseDate || release.createdAt)}
                </h2>
                <Badge className={statusColors[release.status as Status]}>{release.status}</Badge>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-white text-black hover:bg-gray-100"
                    onClick={() => {
                      setEditingRelease(release);
                      setShowReleaseForm(true);
                    }}
                  >
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-white text-red-500 hover:bg-red-50"
                    onClick={() => setShowDeleteReleaseConfirm(true)}
                  >
                    <Trash size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          <CardContent className="p-4 border-b space-y-6">
            {/* Two-column layout for details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                <DetailsItem icon={<Globe size={16} />} label="Environment">
                  <Badge className={environmentColors[release.environment as Environment]}>
                    {release.environment}
                  </Badge>
                </DetailsItem>
                
                <DetailsText 
                  text={release.approvedBy || 'Not approved yet'} 
                  label="Approved By" 
                  icon={<User size={16} />}
                />

                <DetailsItem icon={<Clock size={16} />} label="Created">
                  <p className="text-sm">{formatDate(release.createdAt)}</p>
                </DetailsItem>
              </div>
              
              {/* Right column */}
              <div className="space-y-4">
                <DetailsLink 
                  href={release.brdLink} 
                  label="BRD Link" 
                />
                
                <DetailsText 
                  text={release.prework || 'Not checked yet'} 
                  label="Pre-Work Site Check by" 
                  icon={<User size={16} />}
                />

                {release.plannedReleaseDate && (
                  <DetailsItem icon={<Calendar size={16} />} label="Planned Release Date">
                    <p className="text-sm">{formatDate(release.plannedReleaseDate)}</p>
                  </DetailsItem>
                )}
              </div>
            </div>
            
            {/* Full-width notes */}
            {release.notes && (
              <div className="mt-6">
                <div className="bg-white p-4 rounded-md border border-gray-100 shadow-sm">
                  <MarkdownContent content={release.notes} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sub-items section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Release Items ({release.subItems.length})</CardTitle>
              <CardDescription>
                Detailed view of all items included in this release
              </CardDescription>
            </div>
            {isAdmin && release.status !== 'released' && (
              <Button onClick={() => {
                if (subItemListRef.current) {
                  subItemListRef.current.openAddItemForm();
                }
              }}>
                <Plus size={16} className="mr-2" /> Add Item
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="w-full">
              <SubItemList 
                ref={subItemListRef}
                subItems={release.subItems} 
                updateSubItem={updateSubItem}
                deleteSubItem={deleteSubItem}
                addSubItem={addSubItem}
                canEdit={isAdmin}
                releaseId={releaseId || ''}
                clientId={clientId}
                showActions={isAdmin}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      {showReleaseForm && (
        <NativeDialog open={showReleaseForm} onOpenChange={(open) => {
          if (!open) {
            setEditingRelease(null);
          }
          setShowReleaseForm(open);
        }}>
          <NativeDialogContent>
            <InlineReleaseForm
              release={editingRelease}
              onSubmit={(updatedRelease) => {
                const fullUpdatedRelease = {
                  ...updatedRelease,
                  id: editingRelease?.id || ''
                };
                updateRelease(fullUpdatedRelease);
                setShowReleaseForm(false);
                setEditingRelease(null);
              }}
              onCancel={() => {
                setShowReleaseForm(false);
                setEditingRelease(null);
              }}
            />
          </NativeDialogContent>
        </NativeDialog>
      )}
      
      {showDeleteReleaseConfirm && (
        <NativeDialog open={showDeleteReleaseConfirm} size="xl" onOpenChange={setShowDeleteReleaseConfirm}>
          <NativeDialogContent>
            <NativeDialogHeader>
              <NativeDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Confirm Deletion
              </NativeDialogTitle>
              <NativeDialogDescription>
                Are you sure you want to delete this release and all its items? This action cannot be undone.
              </NativeDialogDescription>
            </NativeDialogHeader>
            <NativeDialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowDeleteReleaseConfirm(false)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={deleteRelease} 
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete Release'}
              </Button>
            </NativeDialogFooter>
          </NativeDialogContent>
        </NativeDialog>
      )}
    </div>
  );
};

export default ReleasePage;
